import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatError } from "@/lib/api";
import AIAssistBar from "@/components/ai/AIAssistBar";
import { ArrowLeft, Send, Sparkles, CheckCircle } from "lucide-react";

export default function PreSaleWizardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const [params] = useSearchParams();
  const sellerId = params.get("seller");
  const listingId = params.get("listing");

  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [requestReady, setRequestReady] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitWarning, setSubmitWarning] = useState(null);
  const [error, setError] = useState("");
  // AVS-Awareness for UI hint banners. Initialised from auth context, then synced from backend response.
  const [senderAvsVerified, setSenderAvsVerified] = useState(
    user?.age_verification_status === "VERIFIED"
  );
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!user || user === false) { navigate("/login"); return; }
    if (!sellerId) { navigate(-1); return; }
  }, [user, navigate, sellerId]);

  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = async () => {
    if (!text.trim() || sending) return;
    const myText = text.trim();
    setText("");
    setSending(true);
    setError("");
    setMessages(prev => [...prev, { sender: "BUYER", text: myText, message_id: Date.now().toString() }]);
    try {
      const { data } = await api.post("/stilltouchedx/presale-wizard", {
        seller_id: sellerId,
        listing_id: listingId || undefined,
        message: myText,
        session_id: sessionId || undefined,
        locale: i18n.language,
      });
      setSessionId(data.session_id);
      setRequestReady(data.request_ready);
      if (typeof data.sender_avs_verified === "boolean") {
        setSenderAvsVerified(data.sender_avs_verified);
      }
      setMessages(prev => [...prev, { sender: "ASSISTANT", text: data.ai_response, message_id: uid() }]);
    } catch (err) {
      setError(formatError(err));
    } finally {
      setSending(false);
    }
  };

  const submitRequest = async () => {
    if (!sessionId) return;
    setSending(true);
    try {
      const { data } = await api.post(`/stilltouchedx/presale-wizard/${sessionId}/send`);
      const mod = data?.moderation;
      if (mod && (mod.decision === "REDACTED" || mod.redaction_count > 0)) {
        const c = mod.redaction_count;
        const kind = c === 1 ? t("presale.redact_kind_singular") : t("presale.redact_kind_plural");
        setSubmitWarning(t("presale.redact_notice", { count: c || "", kind }));
      }
      setSubmitted(true);
    } catch (err) {
      setError(formatError(err));
    } finally {
      setSending(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="presale-wizard-done" className="text-stx-text min-h-full flex flex-col items-center justify-center px-6 py-16">
        <CheckCircle size={48} className="text-green-400 mb-4" />
        <h2 className="text-lg font-bold">{t("presale.sent_title")}</h2>
        <p className="text-sm text-stx-text-muted mt-2 text-center leading-relaxed">
          {t("presale.sent_body")}
        </p>
        {submitWarning && (
          <div data-testid="wizard-redact-warning" className="mt-5 px-3 py-3 bg-amber-500/15 border border-amber-500/40 max-w-md">
            <p className="text-[11px] text-amber-600 dark:text-amber-200 leading-relaxed">{submitWarning}</p>
          </div>
        )}
        <button
          data-testid="presale-back-btn"
          onClick={() => navigate(-1)}
          className="mt-6 h-10 px-6 bg-stx-accent text-stx-accent-fg text-sm font-medium hover:bg-stx-accent-hover transition-colors"
        >
          {t("presale.back")}
        </button>
      </div>
    );
  }

  return (
    <div data-testid="presale-wizard-page" className="text-stx-text min-h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-stx-border shrink-0">
          <button data-testid="presale-wizard-back" onClick={() => navigate(-1)} className="text-stx-text">
            <ArrowLeft size={20} />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-sm font-bold text-stx-text flex items-center gap-1.5">
              <Sparkles size={14} className="text-stx-accent" /> {t("presale.title")}
            </h1>
            <p className="text-[10px] text-stx-text-dim">{t("presale.subtitle")}</p>
          </div>
        </div>

        {/* AVS-Hinweis-Banner (oben, dauerhaft sichtbar wenn Käufer nicht AVS) */}
        {!senderAvsVerified && (
          <div data-testid="presale-avs-banner" className="shrink-0 px-4 py-3 bg-amber-500/15 border-b border-amber-500/40">
            <p className="text-[11px] text-amber-700 dark:text-amber-200 leading-relaxed">
              <span className="font-semibold">{t("presale.avs_banner_prefix")}</span>{" "}
              {t("presale.avs_banner_body")}
            </p>
            <button
              data-testid="presale-avs-cta"
              onClick={() => navigate("/profile/security")}
              className="mt-2 inline-flex items-center gap-1 h-8 px-3 bg-amber-500 text-[#0A0A0A] text-[11px] font-semibold hover:bg-amber-400 transition-colors"
            >
              <CheckCircle size={12} /> {t("presale.avs_cta")}
            </button>
          </div>
        )}

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
          {messages.length === 0 && (
            <div className="text-center py-8">
              <Sparkles size={28} className="mx-auto text-stx-text-dim mb-3" />
              <p className="text-sm text-stx-text-muted">{t("presale.empty")}</p>
              <p className="text-[10px] text-stx-text-dim mt-1">{t("presale.empty_hint")}</p>
            </div>
          )}
          {messages.map((msg) => {
            let displayText = msg.text;
            let finalRequest = null;
            if (msg.sender === "ASSISTANT" && msg.text) {
              const m = msg.text.match(/\[FINAL_REQUEST\]\s*([\s\S]*?)\s*\[\/FINAL_REQUEST\]/i);
              if (m) {
                finalRequest = m[1].trim();
                displayText = msg.text.replace(/\[FINAL_REQUEST\][\s\S]*?\[\/FINAL_REQUEST\]/i, "").trim();
              }
            }
            return (
              <div key={msg.message_id} className={`flex ${msg.sender === "BUYER" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-3 py-2 ${
                  msg.sender === "BUYER"
                    ? "bg-stx-accent text-stx-accent-fg"
                    : "bg-stx-panel text-stx-text border border-stx-border"
                }`}>
                  {msg.sender === "ASSISTANT" && (
                    <p className="text-[9px] text-stx-text-dim font-medium mb-1 flex items-center gap-1">
                      <Sparkles size={8} /> {t("presale.assistant")}
                    </p>
                  )}
                  {displayText && <p className="text-sm leading-relaxed whitespace-pre-wrap">{displayText}</p>}
                  {finalRequest && (
                    <div className="mt-2 p-2 bg-stx-bg border-l-2 border-green-400">
                      <p className="text-[9px] text-green-500 font-bold uppercase tracking-wider mb-1">{t("presale.final_request_header")}</p>
                      <p className="text-sm text-stx-text leading-relaxed whitespace-pre-wrap">{finalRequest}</p>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          {sending && (
            <div className="flex justify-start">
              <div className="px-3 py-2 bg-stx-bg-elevated border border-stx-border">
                <div className="flex gap-1">
                  <div className="w-1.5 h-1.5 bg-stx-text-dim rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                  <div className="w-1.5 h-1.5 bg-stx-text-dim rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                  <div className="w-1.5 h-1.5 bg-stx-text-dim rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            </div>
          )}
          {error && <p className="text-xs text-red-500 text-center">{error}</p>}
          <div ref={scrollRef} />
        </div>

        {/* Ready Banner */}
        {requestReady && (
          <div className="shrink-0 px-4 py-3 bg-green-500/15 border-t border-green-500/40">
            {!senderAvsVerified && (
              <div className="mb-3 p-2 bg-amber-500/15 border-l-2 border-amber-500">
                <p className="text-[11px] text-amber-700 dark:text-amber-200 leading-relaxed">{t("presale.ready_hint_avs")}</p>
                <button
                  data-testid="presale-avs-cta-ready"
                  onClick={() => navigate("/profile/security")}
                  className="mt-2 text-[11px] font-semibold text-amber-600 dark:text-amber-300 underline hover:text-amber-500 dark:hover:text-amber-200"
                >
                  {t("presale.avs_cta_ready")}
                </button>
              </div>
            )}
            <p className="text-xs text-green-700 dark:text-green-300 mb-2">{t("presale.ready_msg")}</p>
            <button
              data-testid="presale-submit-btn"
              onClick={submitRequest}
              disabled={sending}
              className="w-full h-10 bg-green-600 text-white text-sm font-medium flex items-center justify-center gap-1.5 hover:bg-green-500 transition-colors disabled:opacity-50"
            >
              <Send size={14} /> {t("presale.submit")}
            </button>
          </div>
        )}

        {/* Input — unified surface: kein extra bg-panel „Kasten", nur border wie der Body */}
        <div className="shrink-0 border-t border-stx-border px-4 py-3 flex gap-2">
          <input
            data-testid="presale-wizard-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && sendMessage()}
            className="flex-1 h-10 px-3 bg-transparent border border-stx-border text-stx-text text-sm focus:outline-none focus:border-stx-accent placeholder:text-stx-text-dim"
            placeholder={t("presale.placeholder")}
          />
          <button
            data-testid="presale-wizard-send"
            onClick={sendMessage}
            disabled={sending || !text.trim()}
            className="h-10 w-10 bg-stx-accent text-stx-accent-fg flex items-center justify-center disabled:opacity-30 active:scale-[0.95]"
          >
            <Send size={16} />
          </button>
        </div>
        <div className="px-4 pb-3">
          <AIAssistBar area="STX" value={text} onApply={setText} fieldType="description" testIdSuffix="presale" />
        </div>
      </div>
  );
}

function uid() {
  return Math.random().toString(36).substring(2, 10);
}
